/**
 * Browser-profile defaults for the simulated context (docs/sandbox.md §5).
 */
import type { DeviceProfile, SimContext } from "../../context";
import type { SeedCookie } from "../../cookies";
import {
  DECOY_PRESETS,
  DEFAULT_DECOY_PRESET,
  DEFAULT_DEVICE_PROFILE,
  DEVICE_PROFILES,
} from "./profiles";

/** A deep copy of a device profile, so contexts never share nested objects. */
function structuredCopy(device: DeviceProfile | undefined): DeviceProfile {
  if (!device) throw new Error("Unknown default device profile");
  return { ...device, screen: { ...device.screen }, viewport: { ...device.viewport } };
}

/** The simulated page the code runs on: a storefront checkout. */
export const DEFAULT_PAGE_URL = "https://shop.example/checkout";

/** How long a simulated network request or resource load takes, in virtual ms. */
export const SIMULATED_LATENCY_MS = 30;

/** Marks elements the script created, in the final HTML for the Page preview. */
export const INJECTED_ATTRIBUTE = "data-afterimage-injected";

/** The checkout page's initial HTML, with a payment form for skimmers to find. */
export const DEFAULT_PAGE_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Checkout · Example Shop</title>
<link rel="stylesheet" href="/assets/shop.css">
</head>
<body>
<header id="site-header"><a href="/" class="logo">Example Shop</a><nav><a href="/cart">Cart (2)</a></nav></header>
<main id="content">
<h1>Checkout</h1>
<section id="order-summary"><ul class="items"><li>Linen shirt · $48.00</li><li>Canvas tote · $22.00</li></ul><p class="total">Total: $70.00</p></section>
<form id="checkout" action="/checkout/pay" method="post">
<fieldset id="contact"><legend>Contact</legend>
<label for="email">Email</label><input id="email" name="email" type="email" autocomplete="email">
</fieldset>
<fieldset id="payment"><legend>Payment</legend>
<label for="cc-name">Name on card</label><input id="cc-name" name="cc-name" autocomplete="cc-name">
<label for="cc-number">Card number</label><input id="cc-number" name="cc-number" inputmode="numeric" autocomplete="cc-number">
<label for="cc-exp">Expiry</label><input id="cc-exp" name="cc-exp" autocomplete="cc-exp" placeholder="MM / YY">
<label for="cc-csc">Security code</label><input id="cc-csc" name="cc-csc" inputmode="numeric" autocomplete="cc-csc">
</fieldset>
<button id="pay" type="submit">Pay $70.00</button>
</form>
</main>
<footer><p>© Example Shop</p></footer>
</body>
</html>
`;

/** The storefront's cookies: analytics, a readable session id, and an HttpOnly auth cookie. */
export const DEFAULT_COOKIES: SeedCookie[] = [
  { name: "_ga", value: "GA1.1.1846293741.1760270400", domain: ".shop.example" },
  { name: "session_id", value: "6f1c2e9a-4b7d-4e21-9c3a-7d5e8f0a1b2c" },
  { name: "cart_id", value: "c_8231" },
  { name: "__Host-auth", value: "v2.Zm9vYmFyYmF6", httpOnly: true, secure: true },
];

export const DEFAULT_LOCAL_STORAGE: Record<string, string> = {
  auth_token:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1XzQ4MTkiLCJlbWFpbCI6ImpvcmRhbi5sZWVAZXhhbXBsZS5jb20ifQ.c2lnbmF0dXJlLW5vdC1yZWFs",
  cart: '{"items":2,"total":70}',
  theme: "light",
};

export const DEFAULT_SESSION_STORAGE: Record<string, string> = { checkout_step: "payment" };

/** The default context for a run at `now` (epoch ms). */
export const defaultContext = (now: number): SimContext => ({
  now,
  url: DEFAULT_PAGE_URL,
  referrer: "https://search.example/?q=linen+shirt",
  html: DEFAULT_PAGE_HTML,
  languages: ["en-US", "en"],
  timezone: "UTC",
  device: structuredCopy(DEVICE_PROFILES[DEFAULT_DEVICE_PROFILE]?.device),
  decoys: [...(DECOY_PRESETS[DEFAULT_DECOY_PRESET]?.decoys ?? [])],
  cookies: DEFAULT_COOKIES.map((c) => ({ ...c })),
  localStorage: { ...DEFAULT_LOCAL_STORAGE },
  sessionStorage: { ...DEFAULT_SESSION_STORAGE },
  mockRoutes: [],
});
